import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Modal,
    Dropdown,
    ButtonGroup
} from "react-bootstrap";

const BASE =
    process.env.REACT_APP_API_URL || "https://news-portal-l6pf.onrender.com";

const API_URL = `${BASE}/api/news`;
const IMAGE_URL = `${BASE}/uploads/`;

const Health = () => {
    const [healthNews, setHealthNews] = useState([]);
    const [show, setShow] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);

    useEffect(() => {
        fetchHealthNews();
    }, []);

    const getImage = (item) => {
        if (item?.imagePath?.startsWith("http")) return item.imagePath;
        if (item?.image) return `${IMAGE_URL}${item.image}`;
        return "https://via.placeholder.com/400x250?text=No+Image";
    };

    const getViews = (item) => {
        const v =
            item?.views ??
            item?.viewCount ??
            item?.totalViews ??
            item?.impressions ??
            0;
        return Number(v) || 0;
    };

    const formatViews = (n) => {
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
        return n;
    };

    const fetchHealthNews = async () => {
        try {
            const res = await fetch(API_URL);
            const result = await res.json();
            const data = result?.data || [];

            const health = data.filter((item) =>
                ["health", "fitness", "medical"].includes(
                    item.category?.toLowerCase()
                )
            );

            setHealthNews(health);
        } catch (err) {
            console.error("Health Fetch Error:", err);
        }
    };

    const handleShow = (item) => {
        const id = item?._id || item?.id;
        const nextViews = getViews(item) + 1;

        if (id) {
            setHealthNews((prev) =>
                prev.map((n) =>
                    (n._id === id || n.id === id)
                        ? { ...n, views: getViews(n) + 1 }
                        : n
                )
            );
        }

        setSelectedNews({ ...item, views: nextViews });
        setShow(true);

        fetch(`${API_URL}/${id}/view`, {
            method: "PUT"
        }).catch(() => { });
    };

    const handleClose = () => setShow(false);

    const getShareUrl = () => window.location.href;

    const openNewTab = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert("Link copied!");
        } catch {
            window.prompt("Copy this link:", text);
        }
    };

    const shareTo = (platform, post) => {
        const url = getShareUrl();
        const title = post?.title || "News";

        switch (platform) {
            case "whatsapp":
                openNewTab(`https://wa.me/?text=${encodeURIComponent(title + " " + url)}`);
                break;
            case "facebook":
                openNewTab(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
                break;
            case "x":
                openNewTab(`https://twitter.com/intent/tweet?text=${title}&url=${url}`);
                break;
            case "telegram":
                openNewTab(`https://t.me/share/url?url=${url}&text=${title}`);
                break;
            case "linkedin":
                openNewTab(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`);
                break;
            case "copy":
                copyToClipboard(url);
                break;
            default:
                break;
        }
    };

    const ShareMenu = ({ post }) => (
        <Dropdown as={ButtonGroup}>
            <Button size="sm" variant="danger" onClick={() => shareTo("whatsapp", post)}>
                <i className="bi bi-share-fill me-1"></i> Share
            </Button>

            <Dropdown.Toggle split size="sm" variant="danger" />

            <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => shareTo("whatsapp", post)}>
                    WhatsApp
                </Dropdown.Item>

                <Dropdown.Item onClick={() => shareTo("facebook", post)}>
                    Facebook
                </Dropdown.Item>

                <Dropdown.Item onClick={() => shareTo("x", post)}>
                    X
                </Dropdown.Item>

                <Dropdown.Item onClick={() => shareTo("telegram", post)}>
                    Telegram
                </Dropdown.Item>

                <Dropdown.Item onClick={() => shareTo("linkedin", post)}>
                    LinkedIn
                </Dropdown.Item>

                <Dropdown.Divider />

                <Dropdown.Item onClick={() => shareTo("copy", post)}>
                    Copy Link
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );

    return (
        <>
            <div className="health-hero text-white text-center py-5">
                <Container>
                    <h1 className="fw-bold display-4">Health News</h1>
                    <p className="lead">
                        Stay informed with health tips, fitness trends & medical updates
                    </p>
                </Container>
            </div>

            <Container className="py-5">
                <Row className="g-4">
                    {healthNews.map((news, index) => (
                        <Col md={4} key={index}>
                            <Card className="health-card h-100">

                                <Card.Img src={getImage(news)} className="health-img" />

                                <Card.Body>
                                    <div className="d-flex justify-content-between mb-2">
                                        <Badge bg="danger">{news.category}</Badge>

                                        <span className="news-views">
                                            <i className="bi bi-eye me-1"></i>
                                            {formatViews(getViews(news))}
                                        </span>
                                    </div>

                                    <Card.Title>{news.title}</Card.Title>

                                    <Card.Text className="text-muted">
                                        {news.shortDesc}
                                    </Card.Text>

                                    <div className="d-flex gap-2">
                                        <Button size="sm" variant="danger" onClick={() => handleShow(news)}>
                                            Read More
                                        </Button>

                                        <ShareMenu post={news} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedNews?.title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedNews && (
                        <>
                            <img
                                src={getImage(selectedNews)}
                                className="w-100 rounded mb-3"
                                alt=""
                            />

                            <p>{selectedNews.content}</p>

                            <div className="d-flex justify-content-between">
                                <span>
                                    <i className="bi bi-eye me-1"></i>
                                    {formatViews(getViews(selectedNews))}
                                </span>

                                <ShareMenu post={selectedNews} />
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <style>
                {`
                .health-hero {
                    background: linear-gradient(135deg, #dc3545, #6c0d14);
                }

                .health-card {
                    border-radius: 14px;
                    transition: 0.3s;
                    box-shadow: 0 6px 18px rgba(0,0,0,0.08);
                }

                .health-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 18px 40px rgba(0,0,0,0.15);
                }

                .health-img {
                    height: 220px;
                    object-fit: cover;
                }

                .news-views {
                    font-size: 12px;
                    font-weight: 700;
                    color: #6c757d;
                }
                `}
            </style>
        </>
    );
};

export default Health;